import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight, Home, Briefcase, Dumbbell, Music } from "lucide-react";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OutbuildsPage = () => {
  const [outbuildProjects, setOutbuildProjects] = useState([]);

  const fetchOutbuildProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects?category=outbuilds`);
      setOutbuildProjects(response.data);
    } catch {
      // Silently handle error
    }
  }, []);

  useEffect(() => {
    fetchOutbuildProjects();
  }, [fetchOutbuildProjects]);

  const uses = [
    {
      icon: Briefcase,
      title: "Home Office",
      description: "Create the perfect work from home environment, separate from your main living space.",
    },
    {
      icon: Dumbbell,
      title: "Home Gym",
      description: "Build your own private fitness space with all the room you need for equipment.",
    },
    {
      icon: Music,
      title: "Entertainment Room",
      description: "The perfect man cave or entertainment space for gaming, music, or relaxation.",
    },
    {
      icon: Home,
      title: "Guest Suite",
      description: "Additional accommodation for guests with complete privacy and comfort.",
    },
  ];

  const features = [
    "Fully insulated walls and roof",
    "Double glazed windows and doors",
    "Electrical installation available",
    "Various sizes to suit your garden",
    "Planning permission advice",
    "Custom designs available",
    "Quick installation",
    "10 year structural warranty",
  ];

  return (
    <div data-testid="outbuilds-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Garden Rooms Lanarkshire | Outbuildings Motherwell, Hamilton, Glasgow"
        description="Garden rooms & outbuildings in Motherwell, Hamilton, Glasgow & Lanarkshire. Home offices, gyms, man caves. Fully insulated, double glazed. 10 year warranty. Free quotes - call 07917 181597"
        keywords={`garden rooms, outbuildings, garden office, home office, man cave, garden gym, summer house, garden rooms Lanarkshire, garden rooms Glasgow, garden rooms Motherwell, garden rooms Hamilton, garden buildings, outdoor rooms, ${LOCATION_KEYWORDS}`}
        canonical="/outbuilds"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0A0A0A]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://static.wixstatic.com/media/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg/v1/fill/w_700,h_1000,al_c,q_80,enc_avif,quality_auto/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1
              className="text-4xl sm:text-5xl font-black text-white mb-6"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              GARDEN ROOMS &{" "}
              <span className="text-[#FF6B00]">OUTBUILDS</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              Create additional living space with our premium garden rooms and
              outbuildings. Perfect for home offices, gyms, man caves, or guest
              accommodation. Fully insulated with <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazed windows</Link> and <Link to="/doors" className="text-[#FF6B00] hover:underline">quality doors</Link>, built to last.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="outbuilds-cta"
                className="bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all text-center"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:07917181597"
                className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                07917 181597
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Uses Section */}
      <section data-testid="uses-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              PERFECT <span className="text-[#FF6B00]">FOR</span>
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              Our garden rooms can be tailored to suit any purpose. Here are just
              a few popular uses for our outbuildings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {uses.map((use) => (
              <div
                key={use.title}
                data-testid={`use-card-${use.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#F5F5F5] p-8 border border-[#E5E5E5] hover:border-[#FF6B00] transition-all group"
              >
                <div className="w-16 h-16 bg-[#0A0A0A] flex items-center justify-center mb-6 group-hover:bg-[#FF6B00] transition-colors">
                  <use.icon className="w-8 h-8 text-white" />
                </div>
                <h3
                  className="text-lg font-black text-[#0A0A0A] mb-3"
                  style={{ fontFamily: "'Chivo', sans-serif" }}
                >
                  {use.title}
                </h3>
                <p className="text-[#525252] text-sm">{use.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2
                className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                QUALITY <span className="text-[#FF6B00]">FEATURES</span>
              </h2>
              <p className="text-[#525252] mb-8">
                Our garden rooms are built to the highest standards using quality
                materials. Each building is fully insulated and weatherproof,
                designed to be used all year round. Fitted with <Link to="/windows" className="text-[#FF6B00] hover:underline">energy-efficient windows</Link> and <Link to="/doors" className="text-[#FF6B00] hover:underline">secure doors</Link> as standard.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature) => (
                  <div
                    key={feature}
                    data-testid={`outbuild-feature-${feature.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#0A0A0A] text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.wixstatic.com/media/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg/v1/fill/w_700,h_1000,al_c,q_80,enc_avif,quality_auto/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg"
                alt="Garden Room"
                className="w-full max-w-lg mx-auto"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0A0A0A] text-white p-6">
                <p className="font-black text-2xl">10 YEAR</p>
                <p className="text-sm text-gray-400">Structural Warranty</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              HOW IT <span className="text-[#FF6B00]">WORKS</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Consultation",
                description: "We visit your property to discuss your requirements and take measurements.",
              },
              {
                step: "02",
                title: "Design",
                description: "We create a custom design tailored to your needs and garden space.",
              },
              {
                step: "03",
                title: "Quote",
                description: "You receive a detailed, no-obligation quote for your garden room.",
              },
              {
                step: "04",
                title: "Installation",
                description: "Our expert team builds your garden room quickly and professionally.",
              },
            ].map((item) => (
              <div
                key={item.step}
                data-testid={`process-step-${item.step}`}
                className="relative"
              >
                <div className="text-7xl font-black text-[#F5F5F5] absolute -top-4 left-0">
                  {item.step}
                </div>
                <div className="relative pt-12 pl-4">
                  <h3
                    className="text-lg font-black text-[#0A0A0A] mb-2"
                    style={{ fontFamily: "'Chivo', sans-serif" }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-[#525252] text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Work */}
      {outbuildProjects.length > 0 && (
        <section className="py-20 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2
                className="text-3xl font-black text-[#0A0A0A]"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                RECENT <span className="text-[#FF6B00]">GARDEN ROOMS</span>
              </h2>
              <Link
                to="/recent-work"
                className="text-[#FF6B00] font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {outbuildProjects.slice(0, 3).map((project) => (
                <div
                  key={project.id}
                  data-testid={`outbuild-project-${project.id}`}
                  className="group bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#FF6B00] transition-all"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0A0A0A]">{project.title}</h3>
                    <p className="text-sm text-[#525252]">{project.description}</p>
                    {project.location && (
                      <p className="text-xs text-[#FF6B00] mt-1">{project.location}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#FF6B00] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            CREATE YOUR PERFECT GARDEN ROOM
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            <Link to="/contact" className="text-white underline hover:text-[#0A0A0A]">Contact us</Link> today for a free consultation and quote. We design and
            install garden rooms fitted with <Link to="/windows" className="text-white underline hover:text-[#0A0A0A]">double glazed windows</Link> and <Link to="/doors" className="text-white underline hover:text-[#0A0A0A]">composite doors</Link> throughout Lanarkshire, Glasgow, and surrounding areas. Read our <Link to="/reviews" className="text-white underline hover:text-[#0A0A0A]">customer reviews</Link>.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
          >
            Request Consultation
          </Link>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Related Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Double Glazing Windows
            </Link>
            <Link to="/doors" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Composite Doors
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              View Our Work
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Garden Room FAQ
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Customer Reviews
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OutbuildsPage;
